// Heart Games Hub Styles - 21 QUESTIONS Style
// Vertical list layout with filter bar

import styled from 'styled-components';
import { theme } from '../theme';

// Main Container
export const HeartGamesHubContainer = styled.div`
  background: var(--background);
  min-height: 100vh;
  width: 100%;
  padding: 0;
  padding-bottom: calc(80px + 60px); /* Bottom nav + filter bar */
  padding-top: 20px; /* Reduced top padding */

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding-bottom: calc(100px + 60px);
    padding-top: 20px;
  }
`;

// Header with title and icons
export const HeartGamesHeader = styled.header`
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--bg-overlay);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border-light);
  padding: 0.5rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.5rem 1rem;
  }
`;

export const HeaderLeftIcon = styled.button`
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: background 0.2s ease;

  &:hover {
    background: var(--bg-secondary);
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const HeartGamesTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1px;
  text-align: center;
  flex: 1;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.25rem;
  }
`;

export const HeaderRightSpacer = styled.div`
  width: 48px; /* Match left icon width for centering */
`;

// Games List Container
export const GamesListContainer = styled.div`
  padding: 0.5rem 1.25rem;
  padding-bottom: 1rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.5rem 1rem;
  }
`;

export const GamesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 0.875rem;
  }
`;

// Game Card - 21 QUESTIONS Style
export const GameCard = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: 16px;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  overflow: hidden;
  box-shadow: ${theme.shadows.light};

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 140, 66, 0.1) 0%, rgba(255, 107, 157, 0.1) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    background: var(--bg-secondary);
    border-color: ${theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};

    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 1rem;
    gap: 0.875rem;
    border-radius: 14px;
  }
`;

// Helper function to convert hex to rgba with opacity
const hexToRgba = (hex: string, opacity: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const GameIcon = styled.div<{ $color: string }>`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: ${({ $color }) => hexToRgba($color, 0.2)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  flex-shrink: 0;
  border: 2px solid ${({ $color }) => hexToRgba($color, 0.4)};

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 56px;
    height: 56px;
    font-size: 1.75rem;
    border-radius: 10px;
  }
`;

export const GameCardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0; /* Prevent overflow */
`;

export const GameCardTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.3;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

export const GameCardDescription = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.4;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.8rem;
  }
`;

// Bottom Filter Bar
export const FilterBar = styled.nav`
  position: fixed;
  bottom: 70px; /* Above bottom navigation */
  left: 0;
  right: 0;
  background: var(--bg-overlay);
  backdrop-filter: blur(20px);
  border-top: 1px solid var(--border-light);
  padding: 0.75rem 1.25rem;
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  z-index: 1000;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    bottom: 70px;
    padding: 0.625rem 1rem;
    gap: 0.375rem;
    /* Account for safe area on iOS */
    padding-bottom: calc(0.625rem + env(safe-area-inset-bottom, 0px));
  }
`;

export const FilterButton = styled.button<{ $isActive: boolean }>`
  background: ${({ $isActive }) =>
    $isActive
      ? theme.colors.gradients.primary
      : 'transparent'};
  color: ${({ $isActive }) => ($isActive ? 'white' : 'var(--text-secondary)')};
  border: ${({ $isActive }) =>
    $isActive ? 'none' : `1px solid var(--border-medium)`};
  border-radius: 20px;
  padding: 0.5rem 1.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: ${({ $isActive }) =>
      $isActive
        ? theme.colors.gradients.primary
        : 'var(--bg-secondary)'};
    color: ${({ $isActive }) => ($isActive ? 'white' : 'var(--text-primary)')};
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.5rem 1rem;
    font-size: 0.7rem;
  }
`;

// Empty State
export const GamesEmptyState = styled.div`
  text-align: center;
  padding: 3rem 1.5rem;
  color: var(--text-secondary);

  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 0.5rem 0;
  }

  p {
    font-size: 0.875rem;
    margin: 0;
  }
`;

// Loading State
export const GamesLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
`;


// Your Heart Profile — the way back to a result you already earned.
//
// Sits above the game list rather than in it, because it is not a game: it is
// the thing the games produced. Styled as a distinctly warmer card so it reads
// as "yours" instead of "another one to play".
export const HeartProfileCard = styled.div`
  background: linear-gradient(135deg, rgba(255, 140, 66, 0.18) 0%, rgba(255, 107, 157, 0.12) 100%);
  border: 1px solid rgba(255, 140, 66, 0.45);
  border-radius: 16px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: ${theme.shadows.light};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
    border-color: ${theme.colors.primary};
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

export const HeartProfileCardIcon = styled.div`
  font-size: 2rem;
  flex-shrink: 0;
  line-height: 1;
`;

export const HeartProfileCardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const HeartProfileCardTitle = styled.h3`
  margin: 0 0 0.25rem 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-primary);
`;

export const HeartProfileCardText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.4;
`;
